import { getFromLocalStorage, getWindow, removeFromLocalStorage, setToLocalStorage } from '../common/dom-helpers'
import { LocalStorageKeys } from '../environments'
import { isMainWindow } from '../services/container'
import { onWindowUnload } from './helpers/window'
import { isMainExperienceMounted } from '../experiences/helpers'
import { navigateToUrl } from './helpers'
import { getShellLogger } from './logger'
import { SINGLE_SPA_ROUTING_EVENT } from './single-spa-events'

export type StoreCurrentRouteOptions = { readonly defaultRoute?: string }

/**
 * Clear the localStorage, store current route if no experience is mounted to prevent the user from being blocked on a wrong path
 * Redirect to the default route instead
 */
export const routingEventListener = (options?: StoreCurrentRouteOptions) => {
  if (!isMainExperienceMounted()) {
    removeFromLocalStorage(LocalStorageKeys.gotoCurrentRoute)
    const { defaultRoute = '/' } = options ?? {}
    getShellLogger().warn(
      `[URL]: "${location.pathname}" does not have a supported extension. Redirecting to [DEFAULT URL]: "${defaultRoute}"`,
    )
    navigateToUrl(defaultRoute)
    return
  }
}

/**
 * Init the routing event listener
 * This listener is only setup in the main Window to avoid race storage
 */
export const initStoreCurrentRoute = (options?: StoreCurrentRouteOptions) => {
  if (!isMainWindow()) {
    return
  }

  const listener: EventListener = () => routingEventListener(options)

  getWindow().addEventListener(SINGLE_SPA_ROUTING_EVENT, listener)
  onWindowUnload(() => {
    getWindow().removeEventListener(SINGLE_SPA_ROUTING_EVENT, listener)
  })
}

/**
 * Get the applicable localStorage store current route
 * @returns string
 */
export const getStoreCurrentRoute = (): string | null => getFromLocalStorage(LocalStorageKeys.gotoCurrentRoute)

/**
 * Set the applicable localStorage, store current route
 * @param gotoCurrentRoute
 */
export const setStoreCurrentRoute = (gotoCurrentRoute: string): void =>
  setToLocalStorage(LocalStorageKeys.gotoCurrentRoute, gotoCurrentRoute)

/**
 * Navigate to the current route saved in localStorage
 */
export const navigateToStoredRoute = () => {
  const storedRoute = getStoreCurrentRoute()
  navigateToUrl(storedRoute ?? '/')
}
